import { CheckCircle2, LoaderCircle } from "lucide-react"
import { useForm } from "@inertiajs/react"
import { FormEvent, useState } from "react"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { t } from "@/lib/i18n"

const KEY = "ui.email_verification"

export type VerificationStep = "email" | "code" | "details"

type FieldErrors = Record<string, string | string[] | undefined>

// Errors reach this component from two places with two shapes: the controller
// concern sends a plain string, and `@user.errors.to_hash(true)` sends an array
// of full messages. Same helper as the staff account form.
function errorMessage(value: string | string[] | undefined): string | null {
  if (!value) return null
  return Array.isArray(value) ? value[0] : value
}

interface Props {
  step: VerificationStep
  emailAddress: string | null
  codeLength: number
  codeTtlMinutes: number
  errors: FieldErrors
  sendCodePath: string
  verifyCodePath: string
  testIdPrefix: string
}

/**
 * The two steps that come before a registration form may ask for a name.
 *
 * Shared between the diner sign-up and the staff sign-up because both run the
 * same server flow — `send_code`, then `verify_code`, then the account — and the
 * parts worth getting right here are the parts that would drift if this existed
 * twice.
 *
 * A code typed into this tab, rather than a link mailed out to be clicked, and
 * that is a correctness decision rather than a taste one. On a phone the mail
 * app opens links in its own webview, which carries a different cookie jar. A
 * seated guest upgrading their account would arrive there with no session at
 * all and be minted as a second, separate person — the identity fragmentation
 * `docs/domains/user/features/diner-identity-and-auth.md` rules out. Staying in
 * the tab keeps their table, their bill and their orders attached to them.
 *
 * Step three is deliberately NOT here. The name-and-password form differs
 * between the two surfaces, and each page already owns its own.
 */
export function EmailVerificationSteps({
  step,
  emailAddress,
  codeLength,
  codeTtlMinutes,
  errors,
  sendCodePath,
  verifyCodePath,
  testIdPrefix,
}: Props) {
  // Lets someone on the code step go back and correct a typo in the address.
  // Purely local: submitting a different address re-enters the flow server-side,
  // which is also what abandons the proof held for the old one.
  const [editingEmail, setEditingEmail] = useState(false)

  const showEmailForm = step === "email" || editingEmail

  if (showEmailForm) {
    return (
      <EmailStep
        initialEmail={emailAddress ?? ""}
        error={errorMessage(errors.email_address)}
        sendCodePath={sendCodePath}
        testIdPrefix={testIdPrefix}
      />
    )
  }

  if (step === "code") {
    return (
      <CodeStep
        emailAddress={emailAddress ?? ""}
        codeLength={codeLength}
        codeTtlMinutes={codeTtlMinutes}
        error={errorMessage(errors.code)}
        verifyCodePath={verifyCodePath}
        sendCodePath={sendCodePath}
        onChangeEmail={() => setEditingEmail(true)}
        testIdPrefix={testIdPrefix}
      />
    )
  }

  return (
    <VerifiedBadge
      emailAddress={emailAddress ?? ""}
      onChangeEmail={() => setEditingEmail(true)}
      testIdPrefix={testIdPrefix}
    />
  )
}

function EmailStep({
  initialEmail,
  error,
  sendCodePath,
  testIdPrefix,
}: {
  initialEmail: string
  error: string | null
  sendCodePath: string
  testIdPrefix: string
}) {
  const { data, setData, post, processing } = useForm({ email_address: initialEmail })

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault()
    post(sendCodePath)
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4" data-testid={`${testIdPrefix}-email-step`}>
      <div className="flex flex-col gap-1">
        <h2 className="text-sm font-semibold">{t(`${KEY}.send.title`)}</h2>
        <p className="text-xs text-muted-foreground">{t(`${KEY}.send.description`)}</p>
      </div>

      <div className="flex flex-col gap-2">
        <Label htmlFor="email_address">{t(`${KEY}.send.email_label`)}</Label>
        <Input
          id="email_address"
          name="email_address"
          type="email"
          autoComplete="email"
          placeholder={t(`${KEY}.send.email_placeholder`)}
          value={data.email_address}
          onChange={(event) => setData("email_address", event.target.value)}
          aria-invalid={Boolean(error)}
          data-testid={`${testIdPrefix}-email-input`}
          required
        />
        {error && <p className="text-sm font-medium text-destructive">{error}</p>}
      </div>

      <Button type="submit" className="w-full" disabled={processing} data-testid={`${testIdPrefix}-send-code`}>
        {processing ? t(`${KEY}.send.submit_processing`) : t(`${KEY}.send.submit`)}
        {processing && <LoaderCircle className="animate-spin" />}
      </Button>
    </form>
  )
}

function CodeStep({
  emailAddress,
  codeLength,
  codeTtlMinutes,
  error,
  verifyCodePath,
  sendCodePath,
  onChangeEmail,
  testIdPrefix,
}: {
  emailAddress: string
  codeLength: number
  codeTtlMinutes: number
  error: string | null
  verifyCodePath: string
  sendCodePath: string
  onChangeEmail: () => void
  testIdPrefix: string
}) {
  const { data, setData, post, processing } = useForm({ code: "" })
  const resend = useForm({ email_address: emailAddress })

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault()
    post(verifyCodePath)
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4" data-testid={`${testIdPrefix}-code-step`}>
      <div className="flex flex-col gap-1">
        <h2 className="text-sm font-semibold">{t(`${KEY}.code.title`)}</h2>
        <p className="text-xs text-muted-foreground">
          {t(`${KEY}.code.description`, { length: codeLength, email: emailAddress })}
        </p>
      </div>

      <div className="flex flex-col gap-2">
        <Label htmlFor="code">{t(`${KEY}.code.code_label`)}</Label>
        <Input
          id="code"
          name="code"
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={codeLength}
          className="text-center text-lg tracking-[0.4em] tabular-nums"
          value={data.code}
          // Digits only. The code is numeric, and a pasted "123 456" or a
          // trailing space from a mail client should not read as a wrong guess
          // when guesses are capped.
          onChange={(event) => setData("code", event.target.value.replace(/\D/g, ""))}
          aria-invalid={Boolean(error)}
          data-testid={`${testIdPrefix}-code-input`}
          required
        />
        {error && <p className="text-sm font-medium text-destructive">{error}</p>}
        <p className="text-xs text-muted-foreground">
          {t(`${KEY}.code.expiry_hint`, { minutes: codeTtlMinutes })}
        </p>
      </div>

      <Button type="submit" className="w-full" disabled={processing} data-testid={`${testIdPrefix}-verify-code`}>
        {processing ? t(`${KEY}.code.submit_processing`) : t(`${KEY}.code.submit`)}
        {processing && <LoaderCircle className="animate-spin" />}
      </Button>

      <p className="text-center text-xs text-muted-foreground">{t(`${KEY}.code.no_email_hint`)}</p>

      <div className="flex items-center justify-center gap-4 text-xs">
        <button
          type="button"
          className="underline underline-offset-4"
          onClick={() => resend.post(sendCodePath)}
          disabled={resend.processing}
          data-testid={`${testIdPrefix}-resend-code`}
        >
          {t(`${KEY}.code.resend`)}
        </button>
        <button
          type="button"
          className="underline underline-offset-4"
          onClick={onChangeEmail}
          data-testid={`${testIdPrefix}-change-email`}
        >
          {t(`${KEY}.code.change_email`)}
        </button>
      </div>
    </form>
  )
}

function VerifiedBadge({
  emailAddress,
  onChangeEmail,
  testIdPrefix,
}: {
  emailAddress: string
  onChangeEmail: () => void
  testIdPrefix: string
}) {
  return (
    <div
      className="flex items-center justify-between gap-3 rounded-md border border-border bg-muted/40 px-3 py-2"
      data-testid={`${testIdPrefix}-verified-badge`}
    >
      <div className="flex min-w-0 items-center gap-2">
        <CheckCircle2 className="size-4 shrink-0 text-success" aria-hidden />
        <div className="min-w-0">
          <p className="text-xs text-muted-foreground">{t(`${KEY}.details.verified_badge`)}</p>
          <p className="truncate text-sm font-medium">{emailAddress}</p>
        </div>
      </div>
      <button
        type="button"
        className="shrink-0 text-xs underline underline-offset-4"
        onClick={onChangeEmail}
        data-testid={`${testIdPrefix}-change-email`}
      >
        {t(`${KEY}.details.change_email`)}
      </button>
    </div>
  )
}
